import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Hexagon, Compass, Users, Building2, CreditCard, FileSpreadsheet, UserCog, User, AlertCircle, Sparkles, MessageCircle, Landmark, HandHeart, Plane, Search, Bot, Mail } from 'lucide-react';
import { usePollenCapabilities } from '@/hooks/usePollenCapabilities';
import { API } from '@/App';

const baseTabs = [
  { path: '/pollen-dots-dashboard', label: 'Dashboard', icon: Hexagon },
  { path: '/pollen-dots/queries', label: 'Queries', icon: MessageCircle },
  { path: '/pollen-dots/my', label: 'Profile', icon: User },
  { path: '/pollen-dots/tours', label: 'Tours', icon: Compass },
  { path: '/pollen-dots/customers', label: 'Customers', icon: Users },
  { path: '/pollen-dots/celebrations', label: 'Celebrations', icon: Sparkles },
  { path: '/pollen-dots/care', label: 'Care', icon: HandHeart },
  { path: '/pollen-dots/team', label: 'Team', icon: UserCog },
  { path: '/pollen-dots/vendors', label: 'Database', icon: Building2 },
  { path: '/pollen-dots/payments', label: 'Payments', icon: CreditCard, gate: 'profits' },
  { path: '/pollen-dots/financials', label: 'P&L Reports', icon: FileSpreadsheet, gate: 'pnl' },
  { path: '/pollen-dots/investors', label: 'Investors', icon: Landmark, gate: 'pnl' },
  { path: '/pollen-dots/concepts', label: 'Concepts', icon: Plane },
  { path: '/pollen-dots/seo', label: 'SEO', icon: Search },
  { path: '/pollen-dots/aeo', label: 'AI Visibility', icon: Bot },
  { path: '/pollen-dots/mailbox', label: 'Mailbox', icon: Mail },
];

const PollenDotsNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const caps = usePollenCapabilities();
  const [unverifiedCount, setUnverifiedCount] = useState(0);

  const isActive = (path) => {
    if (path === '/pollen-dots-dashboard') return location.pathname === path;
    // Unified Profile tab — also lights up for `/pollen-dots/profile/:userId`
    // which renders the same component for supervisors viewing teammates.
    if (path === '/pollen-dots/my') {
      return location.pathname.startsWith('/pollen-dots/my')
          || location.pathname.startsWith('/pollen-dots/profile');
    }
    return location.pathname.startsWith(path);
  };

  // Poll the unverified-locations count so the red badge stays in sync after
  // admins pin URLs on the Database → Locations screen.
  const fetchCount = useCallback(async () => {
    try {
      const t = localStorage.getItem('session_token');
      const res = await fetch(`${API}/pollen/locations/unverified-count`, {
        credentials: 'include',
        headers: t ? { Authorization: `Bearer ${t}` } : {},
      });
      if (res.ok) {
        const d = await res.json();
        setUnverifiedCount(Number(d.count || 0));
      }
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => {
    fetchCount();
    const id = setInterval(fetchCount, 20000);
    return () => clearInterval(id);
  }, [fetchCount, location.pathname]);

  const tabs = baseTabs.filter(t => {
    if (t.gate === 'pnl') return caps.can_view_pnl;
    if (t.gate === 'profits') return caps.can_view_profits;
    if (t.gate === 'super_admin') return caps.is_super_admin;
    return true;
  });

  return (
    <div className="flex gap-1 overflow-x-auto pb-1 mb-4 border-b border-border" data-testid="pollen-tabs">
      {tabs.map(({ path, label, icon: Icon }) => {
        const active = isActive(path);
        const isDatabase = path === '/pollen-dots/vendors';
        return (
          <button key={path}
            onClick={() => navigate(path)}
            className={`relative flex items-center gap-1.5 px-3 py-2 rounded-t-lg text-sm font-medium whitespace-nowrap transition-colors
              ${active ? 'bg-primary/10 text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground hover:bg-muted'}`}
            data-testid={`pollen-tab-${label.toLowerCase()}`}>
            <Icon className="w-3.5 h-3.5" />
            {label}
            {isDatabase && unverifiedCount > 0 && (
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  navigate('/pollen-dots/vendors?mode=locations&filter=needs-coords');
                }}
                role="button"
                tabIndex={0}
                title={`${unverifiedCount} locations need a Google Maps pin — click to fix`}
                data-testid="db-unverified-badge"
                className="inline-flex items-center gap-0.5 ml-1 px-1.5 py-0.5 rounded-full bg-rose-500 text-white text-[10px] font-bold leading-none shadow-sm hover:bg-rose-600 hover:shadow cursor-pointer"
              >
                <AlertCircle className="w-2.5 h-2.5" />
                {unverifiedCount}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

export default PollenDotsNav;
